import { useCallback, useEffect, useState } from "react";

import axios from "axios";

import { signedUrlActions } from "../../API";
import { useGetSignedUrlAction } from "../../leak-finder/hooks/useGetSignedUrlAction";
import { S3BucketKeyType, S3BucketKeys } from "../utils/s3helper";

// Check if an S3 object exists by probing a signed URL with a ranged GET.
export const useS3ObjectExists = (
  bucketKey: S3BucketKeyType,
  s3Key?: string | null,
  videoStatus?: string
): { exists: boolean; loading: boolean; checkExists: () => Promise<void> } => {
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);

  const { fetchSignedUrlAction } = useGetSignedUrlAction();

  useEffect(() => {
    checkExists();
  }, []);

  const checkExists = useCallback(async (): Promise<void> => {
    if (!s3Key || !videoStatus) {
      setExists(false);

      setLoading(false);

      return;
    }

    setLoading(true);

    try {
      const signedUrl = await fetchSignedUrlAction({
        objectKey: s3Key.replace(/^\/+/, ""),
        operation: signedUrlActions.getObject,
        bucketKey: bucketKey ?? S3BucketKeys.NODE_FILES,
      });

      if (!signedUrl) {
        setExists(false);

        setLoading(false);

        return;
      }

      const response = await axios.get(signedUrl, {
        // s3 signed url is GET only. Limit data since only header needed.
        headers: { Range: "bytes=0-0", "Cache-Control": "no-cache" },
        // Avoid caching to reflect latest state
        validateStatus: status => {
          return (status >= 200 && status < 300) || status === 416;
        },
      });

      // 206 Partial Content indicates object exists; 200 can happen on some proxies; 416 can also indicate object exists but range unsat.
      const ok = [200, 206, 416].includes(response.status);

      setExists(ok);
    } catch {
      setExists(false);
    } finally {
      setLoading(false);
    }
  }, [s3Key, bucketKey, fetchSignedUrlAction]);

  return { exists, loading, checkExists };
};
